/**
 * ROW 847, the screen's copy of api/src/lib/send-destination.ts. His word, option "A now, B later",
 * relayed by the program office 4 Oct 2026: "A NUMBER PICKER in the text box (Care only): the choices
 * are the case's number and the record's two numbers, each shown once; the default stays exactly
 * today's; the destination is named on the button as now; the access log records which number was
 * chosen. No platform change."
 *
 * The rule is the server's, and the server is the gate: it sends to a named number only when it is the
 * case's own or one of the matched member's record numbers, and refuses any other by name. This copy
 * only decides what the Text box OFFERS, so it can never offer a number the server would refuse. Pinned
 * in lockstep with the API's copy by tests/unit/number-picker.test.tsx:
 *   - numbers compare by their 10-digit key (the last ten digits);
 *   - today's order is the order of precedence and the default's: the case's own number, then the
 *     record's phone1, then its phone2;
 *   - a number that is two of them at once is offered ONCE, named by the first and labelled with both.
 */
export type DestinationSource = 'case' | 'record-phone1' | 'record-phone2';

/** Today's order, which is also the default's. */
export const DESTINATION_ORDER: readonly DestinationSource[] = ['case', 'record-phone1', 'record-phone2'];

/** What the picker calls each source. */
export const DESTINATION_WORDING: Readonly<Record<DestinationSource, string>> = {
  case: "this case's number",
  'record-phone1': 'record phone 1',
  'record-phone2': 'record phone 2',
};

const PHONE_RE = /^\d{10}$/;

/** The 10-digit key a number is compared by (the server's normKey). */
export function normKey(phone: unknown): string {
  return String(phone ?? '').replace(/\D/g, '').slice(-10);
}

/** One number the Text box may send to, with every source it stands for, first in today's order. */
export interface DestinationChoice {
  phone: string;
  sources: DestinationSource[];
}

/**
 * The numbers a text from this case may go to: each real 10-digit number ONCE, in today's order. A
 * number that is not ten digits is not offered (the server could not send to it).
 */
export function destinationChoices(n: { casePhone?: unknown; phone1?: unknown; phone2?: unknown }): DestinationChoice[] {
  const keys: Record<DestinationSource, string> = {
    case: normKey(n.casePhone),
    'record-phone1': normKey(n.phone1),
    'record-phone2': normKey(n.phone2),
  };
  const out: DestinationChoice[] = [];
  for (const source of DESTINATION_ORDER) {
    const phone = keys[source];
    if (!PHONE_RE.test(phone)) continue;
    const seen = out.find((c) => c.phone === phone);
    if (seen) seen.sources.push(source);
    else out.push({ phone, sources: [source] });
  }
  return out;
}

/** "this case's number", or "this case's number, also record phone 1" for a number that is both. */
export function choiceLabel(c: DestinationChoice): string {
  const [first, ...rest] = c.sources.map((s) => DESTINATION_WORDING[s]);
  return rest.length ? `${first}, also ${rest.join(' and ')}` : (first ?? '');
}

/** What the compose box shows and sends. */
export interface ComposeDestination {
  /** The number on the "To" line; '' when there is none. */
  shown: string;
  /** The number the send names; '' = name none (the server then uses the record's phone1, then phone2). */
  send: string;
  /** What the picker offers. It shows only when there are two or more. */
  choices: DestinationChoice[];
}

/**
 * The compose box's destination.
 *
 * TODAY'S PANEL, EXACTLY, when the conversation read carries no labelled record numbers, or when the
 * case's own number is not a 10-digit number: the "To" line is the case's number, else the first
 * roster number, and the send names the case's number as it was handed over (the server answers an
 * unusable one as it always has). Otherwise the choices are the case's number and the record's two,
 * each once; the default is the first of them (today's rule); `picked` moves it to another of them
 * (a picked number that is no longer offered falls back to the default); and the send NAMES the number
 * on the screen, so the server checks exactly what the operator sees.
 */
export function composeDestination(input: {
  casePhone?: string | undefined;
  recordPhones?: { phone1: string; phone2: string } | undefined;
  phones: string[];
  picked: string;
}): ComposeDestination {
  const casePhone = input.casePhone ?? '';
  const today: ComposeDestination = { shown: casePhone || input.phones[0] || '', send: casePhone, choices: [] };
  if (!input.recordPhones) return today;
  const choices = destinationChoices({
    casePhone, phone1: input.recordPhones.phone1, phone2: input.recordPhones.phone2,
  });
  if (casePhone && !choices.some((c) => c.sources.includes('case'))) return today;
  const chosen = choices.find((c) => c.phone === input.picked) ?? choices[0];
  const phone = chosen?.phone ?? '';
  return { shown: phone, send: phone, choices };
}
